// Portfolio "Sizing vs target" verdict: is there an executable trim, and if not, why not.
//
// The rebalance ENGINE is the trim authority. It sets trim_reason ONLY when it stages a
// trim (strategy/rebalance_logic.py: actionable_trim > 0), and that includes the
// Sweet-Spot LEVEL trim on a confirmed defense, where the band target EQUALS the 40%
// funded cap -- the CIO kept that rule 2026-11-02. The page used to infer a trim from a
// target DROP below the cap, which cannot see a level trim, so on 2026-11-03 it called
// the engine's $2.29M Sweet-Spot sell "AT CAP / not a trim" (P2-5).
//
// A funded-cap breach alone is still never a trim (CIO policy 2026-08-13 / 2026-08-27:
// the cap blocks buying, it never sells).

export type PortfolioActionState = "HOLD" | "TRIM" | "ADD" | "AT CAP" | "OVER TARGET";

export interface PortfolioTrimInputs {
  /** computeAllocationAction's market-weight verdict (HOLD / TRIM / ADD). */
  actionState: "HOLD" | "TRIM" | "ADD" | "AT CAP" | "OVER TARGET";
  /** Engine effective target (decimal), null if not published. */
  targetPct: number | null;
  /** Funded/cost-basis cap (decimal). */
  fundedCap: number;
  fundedCapBreached: boolean;
  /** True when the payload came from the fallback path (engine did not evaluate). */
  engineUnavailable: boolean;
  /** Engine trim_reason (null when the engine stages nothing). */
  trimReason: string | null | undefined;
  /** Engine trim amount (suggested_trim_to_target ?? trim_notional). */
  engineTrimAmount: number | null | undefined;
}

export interface PortfolioTrimState {
  executableTrim: boolean;
  /** Engine looked and staged the trim itself. */
  engineStagedTrim: boolean;
  atDeploymentCap: boolean;
  benignDrift: boolean;
  /** Target cut below the cap but the engine staged nothing (sell gate not confirmed). */
  gateNotConfirmed: boolean;
  /** Over target while the engine did not run: no trim was evaluated, so none is shown. */
  unevaluatedOver: boolean;
  dispActionState: PortfolioActionState;
}

const CAP_EPS = 0.005;

export function resolvePortfolioTrimState(i: PortfolioTrimInputs): PortfolioTrimState {
  const targetDropped = typeof i.targetPct === "number" && isFinite(i.targetPct) && i.targetPct < i.fundedCap - CAP_EPS;

  const engineStagedTrim =
    !i.engineUnavailable &&
    typeof i.trimReason === "string" &&
    i.trimReason.startsWith("rebalance_trim:") &&
    typeof i.engineTrimAmount === "number" &&
    isFinite(i.engineTrimAmount) &&
    i.engineTrimAmount > 0;

  // Only the engine's own staged trim is executable. When the engine did not run (weekend,
  // holiday, stale feed, regime unknown) NOTHING was evaluated -- the fallback target may even be
  // a default -- so the page never shows TRIM from it (review P1-3, 2026-11-03); over target
  // reads OVER TARGET with "engine unavailable" beside it.
  const executableTrim = engineStagedTrim;

  const unevaluatedOver = i.engineUnavailable && i.actionState === "TRIM";
  const gateNotConfirmed = !i.engineUnavailable && !executableTrim && i.actionState === "TRIM" && targetDropped;
  const atDeploymentCap = i.fundedCapBreached && !executableTrim && !gateNotConfirmed && !unevaluatedOver;
  const benignDrift =
    i.actionState === "TRIM" && !executableTrim && !atDeploymentCap && !gateNotConfirmed && !unevaluatedOver;

  const dispActionState: PortfolioActionState = executableTrim
    ? "TRIM"
    : gateNotConfirmed || benignDrift || unevaluatedOver
    ? "OVER TARGET"
    : atDeploymentCap
    ? "AT CAP"
    : i.actionState;

  return { executableTrim, engineStagedTrim, atDeploymentCap, benignDrift, gateNotConfirmed, unevaluatedOver, dispActionState };
}
